import { useEffect, useMemo, useRef, useState } from 'react';

import { PageMeta } from '@/components/PageMeta';
import { useSaasStore } from '@/stores/saas-store';
import { CircleDot, ExternalLink, Mail, RefreshCcw, WifiOff, Wrench } from 'lucide-react';

const STATUS_URL = 'https://boulesconnect.statuspage.io/';
const REFRESH_INTERVAL_MS = 45_000;
const DEFAULT_MESSAGE =
  'Nous effectuons une maintenance planifiée. Le site sera de nouveau disponible très prochainement.';

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function PulsingDots() {
  return (
    <div className='mt-6 flex items-center justify-center gap-1.5' aria-hidden='true'>
      <span className='maint-dot maint-dot-0' />
      <span className='maint-dot maint-dot-1' />
      <span className='maint-dot maint-dot-2' />
    </div>
  );
}

function RefreshProgress({ intervalMs }: { intervalMs: number }) {
  const [pct, setPct] = useState(0);
  const start = useRef(0);

  useEffect(() => {
    start.current = Date.now();
    let raf: number;
    const frame = () => {
      const elapsed = Date.now() - start.current;
      const p = Math.min((elapsed / intervalMs) * 100, 100);
      setPct(p);
      if (p < 100) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [intervalMs]);

  return (
    <div className='mt-6 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800' aria-hidden='true'>
      <div
        className='maint-progress-bar h-0.5 rounded-full bg-blue-500 dark:bg-blue-400'
        data-pct={pct}
        ref={el => {
          if (el) el.style.width = pct + '%';
        }}
      />
    </div>
  );
}

export default function Maintenance() {
  const maintenanceMessage = useSaasStore(s => s.maintenanceMessage);

  const message = useMemo(() => {
    const raw = maintenanceMessage?.trim();
    return raw && raw.length > 0 ? raw : DEFAULT_MESSAGE;
  }, [maintenanceMessage]);

  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));

  function obfuscatedMailto(user: string, domain: string, subject: string) {
    window.location.href = 'mai' + 'lto:' + user + '@' + domain + '?subject=' + encodeURIComponent(subject);
  }

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (navigator.onLine) window.location.reload();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className='relative min-h-screen overflow-hidden bg-slate-50 px-6 dark:bg-slate-950'>
      <PageMeta title='Site en maintenance' robots='noindex, nofollow' />

      {/* Animations + dot pattern via CSS only */}
      <style>{`
        .maint-bg-dots {
          background-image: radial-gradient(circle, currentColor 1px, transparent 1px);
          background-size: 28px 28px;
        }
        .maint-ring {
          animation: maint-ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes maint-ping {
          0% { transform: scale(1); opacity: 0.6; }
          75%, 100% { transform: scale(1.35); opacity: 0; }
        }
        .maint-dot {
          display: inline-block;
          height: 8px;
          width: 8px;
          border-radius: 9999px;
          background: currentColor;
        }
        .maint-dot-0 { animation: maint-pulse 1.4s ease-in-out 0s infinite; }
        .maint-dot-1 { animation: maint-pulse 1.4s ease-in-out 0.2s infinite; }
        .maint-dot-2 { animation: maint-pulse 1.4s ease-in-out 0.4s infinite; }
        @keyframes maint-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>

      {/* Dot pattern background */}
      <div
        className='maint-bg-dots pointer-events-none absolute inset-0 text-slate-400 opacity-[0.07] dark:text-slate-500 dark:opacity-[0.05]'
        aria-hidden='true'
      />

      <div className='relative z-10 mx-auto flex min-h-screen w-full max-w-lg flex-col items-center justify-center py-12 text-center'>
        {/* Brand */}
        <header className='mb-10 flex items-center gap-3'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-200 dark:shadow-blue-900/40'>
            <CircleDot className='h-7 w-7 text-white' aria-hidden='true' />
          </div>
          <div className='text-left leading-tight'>
            <div className='text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100'>
              Boules<span className='text-blue-600 dark:text-blue-400'>Connect</span>
            </div>
            <div className='text-[0.7rem] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500'>
              Plateforme de compétitions
            </div>
          </div>
        </header>

        {/* Card */}
        <div className='w-full rounded-2xl border border-slate-200 bg-white/80 px-8 py-10 shadow-xl shadow-slate-200/40 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-slate-950/40'>
          {/* Icon + animated ring */}
          <div className='mb-6 flex justify-center'>
            <div className='relative flex h-20 w-20 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30'>
              <Wrench className='h-10 w-10 text-amber-700 dark:text-amber-300' aria-hidden='true' />
              <span className='maint-ring absolute inset-0 rounded-full border-2 border-amber-400/50 dark:border-amber-500/30' />
            </div>
          </div>

          <h1 className='mb-2 text-2xl font-extrabold text-slate-900 dark:text-slate-100'>Maintenance en cours</h1>

          <p
            className='mx-auto max-w-sm text-base leading-relaxed text-slate-600 dark:text-slate-400'
            aria-live='polite'
          >
            {message}
          </p>

          <div className='text-blue-500 dark:text-blue-400'>
            <PulsingDots />
          </div>

          {/* Connectivity chip */}
          <div
            className={cx(
              'mx-auto mt-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors',
              isOnline
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
            )}
          >
            {isOnline ? (
              <>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-600' aria-hidden='true' />
                Connexion active — rafraîchissement auto
              </>
            ) : (
              <>
                <WifiOff className='h-3.5 w-3.5' aria-hidden='true' />
                Hors ligne — vérifiez votre connexion
              </>
            )}
          </div>

          {isOnline && <RefreshProgress intervalMs={REFRESH_INTERVAL_MS} />}
        </div>

        {/* Actions */}
        <div className='mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
          <button
            type='button'
            onClick={() => window.location.reload()}
            className='cursor-pointer inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm outline-none ring-blue-600/30 transition hover:bg-blue-700 focus-visible:ring-4 active:scale-[.97] sm:w-auto'
          >
            <RefreshCcw className='h-4 w-4' aria-hidden='true' />
            Réessayer maintenant
          </button>

          <a
            href={STATUS_URL}
            target='_blank'
            rel='noreferrer'
            className='inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm outline-none transition hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-slate-400/30 active:scale-[.97] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-800/70 sm:w-auto'
          >
            État des services
            <ExternalLink className='h-4 w-4' aria-hidden='true' />
          </a>

          <button
            type='button'
            onClick={() => obfuscatedMailto('support', 'boulesconnect.fr', 'Maintenance BoulesConnect')}
            className='cursor-pointer inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm outline-none transition hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-slate-400/30 active:scale-[.97] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-800/70 sm:w-auto'
          >
            <Mail className='h-4 w-4' aria-hidden='true' />
            Contacter le support
          </button>
        </div>

        <footer className='mt-10 space-y-1 text-sm text-slate-400 dark:text-slate-600'>
          <p>Merci de votre patience — l'équipe BoulesConnect 💙</p>
          <p className='text-xs'>
            Cette page se rafraîchit automatiquement toutes les {Math.round(REFRESH_INTERVAL_MS / 1_000)}s.
          </p>
        </footer>
      </div>
    </div>
  );
}
